import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardBody,
  CardHeader,
  Heading,
  Text,
  VStack,
  HStack,
  SimpleGrid,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  StatArrow,
  useColorModeValue,
  useToast,
  Spinner,
  Alert,
  AlertIcon,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Badge,
  Button,
  InputGroup,
  InputLeftElement,
  Input,
  Select,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  useDisclosure,
  Divider,
  Avatar,
  Flex,
  Switch,
} from '@chakra-ui/react';
import { FiUsers, FiUserCheck, FiUserPlus, FiActivity, FiSearch, FiMail, FiMapPin, FiPhone, FiCalendar, FiEye, FiAward, FiInstagram, FiTrendingUp } from 'react-icons/fi';
import Icon from 'components/icons/Icon';
import { adminService, AdminStatsData, AgencyInfoData, GetAllAgenciesResult, AgencyDetailsData, TrainerSummaryData } from '../../../services/adminService';

function AdminDashboard() {
  const [stats, setStats] = useState<AdminStatsData | null>(null);
  const [agencies, setAgencies] = useState<AgencyInfoData[]>([]);
  const [agenciesLoading, setAgenciesLoading] = useState(true);
  const [statsLoading, setStatsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showActiveOnly, setShowActiveOnly] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);

  // Independent (solo) trainers
  const [soloTrainers, setSoloTrainers] = useState<TrainerSummaryData[]>([]);
  const [soloLoading, setSoloLoading] = useState(true);
  const [soloSearch, setSoloSearch] = useState('');
  const [soloActiveOnly, setSoloActiveOnly] = useState(false);
  const [soloPage, setSoloPage] = useState(1);
  const [soloTotalCount, setSoloTotalCount] = useState(0);

  // Agency details modal
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [selectedAgencyId, setSelectedAgencyId] = useState<number | null>(null);
  const [agencyDetails, setAgencyDetails] = useState<AgencyDetailsData | null>(null);
  const [detailsLoading, setDetailsLoading] = useState(false);
  
  const toast = useToast();

  // Chakra UI theme colors
  const textColor = useColorModeValue("navy.700", "white");
  const textColorSecondary = "gray.400";
  const bg = useColorModeValue("white", "navy.700");
  const cardShadow = useColorModeValue("14px 17px 40px 4px rgba(112, 144, 176, 0.08)", "unset");
  // Hoisted out of JSX - these used to be called inside conditional branches, which breaks hook order
  const borderColor = useColorModeValue("gray.100", "gray.700");
  const headerBg = useColorModeValue("gray.50", "navy.800");
  const inputBg = useColorModeValue("white", "navy.800");
  const inputColor = useColorModeValue("gray.800", "white");
  const inputBorderColor = useColorModeValue("gray.300", "gray.600");
  const inputHoverBorderColor = useColorModeValue("blue.300", "gray.500");
  const inputFocusShadow = useColorModeValue("0 0 0 1px blue.500", "0 0 0 1px blue.400");
  const placeholderColor = useColorModeValue("gray.500", "gray.400");
  const infoAlertBg = useColorModeValue("blue.50", "blue.900");
  const infoAlertBorder = useColorModeValue("blue.200", "blue.600");
  const errorAlertBg = useColorModeValue("red.50", "red.900");
  const errorAlertBorder = useColorModeValue("red.200", "red.600");

  // Load admin stats
  useEffect(() => {
    const loadStats = async () => {
      try {
        setStatsLoading(true);
        const statsData = await adminService.getAdminStats();
        setStats(statsData);
      } catch (error: any) {
        console.error('Failed to load admin stats:', error);
        toast({
          title: "Error",
          description: error.message || "Failed to load admin statistics",
          status: "error",
          duration: 5000,
          isClosable: true,
        });
      } finally {
        setStatsLoading(false);
      }
    };

    loadStats();
  }, [toast]);

  // Load agencies
  useEffect(() => {
    const loadAgencies = async () => {
      try {
        setAgenciesLoading(true);
        const result = await adminService.getAllAgencies({
          page: currentPage,
          limit: 10,
          active_only: showActiveOnly,
          search: searchTerm || undefined,
        });
        setAgencies(result.agencies);
        setTotalCount(result.total_count);
      } catch (error: any) {
        console.error('Failed to load agencies:', error);
        toast({
          title: "Error",
          description: error.message || "Failed to load agencies",
          status: "error",
          duration: 5000,
          isClosable: true,
        });
      } finally {
        setAgenciesLoading(false);
      }
    };

    loadAgencies();
  }, [currentPage, showActiveOnly, searchTerm, toast]);

  // Load independent trainers
  useEffect(() => {
    const loadSoloTrainers = async () => {
      try {
        setSoloLoading(true);
        const result = await adminService.getIndependentTrainers({
          page: soloPage,
          limit: 10,
          active_only: soloActiveOnly,
          search: soloSearch || undefined,
        });
        setSoloTrainers(result.trainers);
        setSoloTotalCount(result.total_count);
      } catch (error: any) {
        console.error('Failed to load independent trainers:', error);
        toast({
          title: "Error",
          description: error.message || "Failed to load independent trainers",
          status: "error",
          duration: 5000,
          isClosable: true,
        });
      } finally {
        setSoloLoading(false);
      }
    };

    loadSoloTrainers();
  }, [soloPage, soloActiveOnly, soloSearch, toast]);

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const handleSearch = (value: string) => {
    setSearchTerm(value);
    setCurrentPage(1); // Reset to first page when searching
  };

  const handleFilterChange = (activeOnly: boolean) => {
    setShowActiveOnly(activeOnly);
    setCurrentPage(1); // Reset to first page when filtering
  };

  // Flip the branded-app entitlement, then let the caller patch its own list.
  const toggleBranding = async (
    agencyId: number,
    enabled: boolean,
    apply: (now: boolean) => void,
  ) => {
    try {
      const now = await adminService.setAgencyBranding(agencyId, enabled);
      apply(now);
      toast({
        title: `Branded app ${now ? "enabled" : "disabled"}`,
        status: "success",
        duration: 3000,
        isClosable: true,
      });
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message || "Failed to update branded-app access",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    }
  };

  const handleViewDetails = async (agencyId: number) => {
    setSelectedAgencyId(agencyId);
    setDetailsLoading(true);
    onOpen();

    try {
      const details = await adminService.getAgencyDetails(agencyId);
      setAgencyDetails(details);
    } catch (error: any) {
      console.error('Failed to load agency details:', error);
      toast({
        title: "Error",
        description: error.message || "Failed to load agency details",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
      onClose(); // Close modal on error
    } finally {
      setDetailsLoading(false);
    }
  };

  if (statsLoading) {
    return (
      <Box pt={{ base: "130px", md: "80px", xl: "80px" }} px={4}>
        <VStack spacing={4} justify="center" align="center" minH="400px">
          <Spinner size="xl" color="brand.500" />
          <Text color={textColorSecondary}>Loading admin dashboard...</Text>
        </VStack>
      </Box>
    );
  }

  return (
    <Box pt={{ base: "130px", md: "80px", xl: "80px" }} px={4}>
      <VStack spacing={6} align="stretch" maxW="1400px" mx="auto">
        {/* Header - removed duplicate title since navbar shows it */}
        <Box>
          <Text color={textColorSecondary} fontSize="lg">
            System overview and agency management
          </Text>
        </Box>

        {/* Statistics Grid */}
        {stats && (
          <SimpleGrid columns={{ base: 1, sm: 2, lg: 3, xl: 5 }} spacing={{ base: 4, md: 6 }}>
            {/* Independent Trainers Card - the solo trainer / influencer segment */}
            <Card
              bg="linear-gradient(135deg, #f7971e 0%, #ff4b6e 100%)"
              color="white"
              borderRadius="20px"
              overflow="hidden"
              position="relative"
              _hover={{ transform: "translateY(-5px)", transition: "all 0.3s" }}
              cursor="pointer"
            >
              <CardBody p={{ base: 4, md: 6 }}>
                <HStack justify="space-between" align="start">
                  <VStack align="start" spacing={2} flex="1">
                    <Text fontSize={{ base: "xs", md: "sm" }} fontWeight="500" opacity={0.9}>
                      Solo Trainers
                    </Text>
                    <Text fontSize={{ base: "2xl", md: "3xl" }} fontWeight="bold" letterSpacing="tight">
                      {stats.independent_trainers}
                    </Text>
                    <HStack spacing={1}>
                      <Icon as={FiTrendingUp} boxSize={3} />
                      <Text fontSize={{ base: "2xs", md: "xs" }} opacity={0.9}>
                        +{stats.new_independent_trainers_30d} in 30 days
                      </Text>
                    </HStack>
                  </VStack>
                  <Box
                    bg="whiteAlpha.200"
                    p={{ base: 2, md: 3 }}
                    borderRadius="16px"
                    backdropFilter="blur(10px)"
                  >
                    <Icon as={FiAward} boxSize={{ base: 5, md: 6 }} />
                  </Box>
                </HStack>
              </CardBody>
            </Card>

            {/* Total Agencies Card */}
            <Card 
              bg="linear-gradient(135deg, #667eea 0%, #764ba2 100%)"
              color="white"
              borderRadius="20px"
              overflow="hidden"
              position="relative"
              _hover={{ transform: "translateY(-5px)", transition: "all 0.3s" }}
              cursor="pointer"
            >
              <CardBody p={{ base: 4, md: 6 }}>
                <HStack justify="space-between" align="start">
                  <VStack align="start" spacing={2} flex="1">
                    <Text fontSize={{ base: "xs", md: "sm" }} fontWeight="500" opacity={0.9}>
                      Total Agencies
                    </Text>
                    <Text fontSize={{ base: "2xl", md: "3xl" }} fontWeight="bold" letterSpacing="tight">
                      {stats.total_agencies}
                    </Text>
                    <HStack spacing={1}>
                      <Icon as={FiUsers} boxSize={3} />
                      <Text fontSize={{ base: "2xs", md: "xs" }} opacity={0.9}>
                        {stats.active_agencies} active
                      </Text>
                    </HStack>
                  </VStack>
                  <Box
                    bg="whiteAlpha.200"
                    p={{ base: 2, md: 3 }}
                    borderRadius="16px"
                    backdropFilter="blur(10px)"
                  >
                    <Icon as={FiUsers} boxSize={{ base: 5, md: 6 }} />
                  </Box>
                </HStack>
              </CardBody>
            </Card>

            {/* Trainers Card */}
            <Card 
              bg="linear-gradient(135deg, #11998e 0%, #38ef7d 100%)"
              color="white"
              borderRadius="20px"
              overflow="hidden"
              position="relative"
              _hover={{ transform: "translateY(-5px)", transition: "all 0.3s" }}
              cursor="pointer"
            >
              <CardBody p={{ base: 4, md: 6 }}>
                <HStack justify="space-between" align="start">
                  <VStack align="start" spacing={2} flex="1">
                    <Text fontSize={{ base: "xs", md: "sm" }} fontWeight="500" opacity={0.9}>
                      Total Trainers
                    </Text>
                    <Text fontSize={{ base: "2xl", md: "3xl" }} fontWeight="bold" letterSpacing="tight">
                      {stats.total_trainers}
                    </Text>
                    <HStack spacing={1}>
                      <Icon as={FiUserCheck} boxSize={3} />
                      <Text fontSize={{ base: "2xs", md: "xs" }} opacity={0.9}>
                        {stats.active_trainers} active
                      </Text>
                    </HStack>
                  </VStack>
                  <Box
                    bg="whiteAlpha.200"
                    p={{ base: 2, md: 3 }}
                    borderRadius="16px"
                    backdropFilter="blur(10px)"
                  >
                    <Icon as={FiUserCheck} boxSize={{ base: 5, md: 6 }} />
                  </Box>
                </HStack>
              </CardBody>
            </Card>

            {/* Clients Card */}
            <Card 
              bg="linear-gradient(135deg, #fa709a 0%, #fee140 100%)"
              color="white"
              borderRadius="20px"
              overflow="hidden"
              position="relative"
              _hover={{ transform: "translateY(-5px)", transition: "all 0.3s" }}
              cursor="pointer"
            >
              <CardBody p={{ base: 4, md: 6 }}>
                <HStack justify="space-between" align="start">
                  <VStack align="start" spacing={2} flex="1">
                    <Text fontSize={{ base: "xs", md: "sm" }} fontWeight="500" opacity={0.9}>
                      Total Clients
                    </Text>
                    <Text fontSize={{ base: "2xl", md: "3xl" }} fontWeight="bold" letterSpacing="tight">
                      {stats.total_clients}
                    </Text>
                    <HStack spacing={1}>
                      <Icon as={FiUserPlus} boxSize={3} />
                      <Text fontSize={{ base: "2xs", md: "xs" }} opacity={0.9}>
                        {stats.active_clients} active relationship
                      </Text>
                    </HStack>
                  </VStack>
                  <Box
                    bg="whiteAlpha.200"
                    p={{ base: 2, md: 3 }}
                    borderRadius="16px"
                    backdropFilter="blur(10px)"
                  >
                    <Icon as={FiUserPlus} boxSize={{ base: 5, md: 6 }} />
                  </Box>
                </HStack>
              </CardBody>
            </Card>

            {/* Workouts Card */}
            <Card 
              bg="linear-gradient(135deg, #f093fb 0%, #f5576c 100%)"
              color="white"
              borderRadius="20px"
              overflow="hidden"
              position="relative"
              _hover={{ transform: "translateY(-5px)", transition: "all 0.3s" }}
              cursor="pointer"
            >
              <CardBody p={{ base: 4, md: 6 }}>
                <HStack justify="space-between" align="start">
                  <VStack align="start" spacing={2} flex="1">
                    <Text fontSize={{ base: "xs", md: "sm" }} fontWeight="500" opacity={0.9}>
                      Total Workouts
                    </Text>
                    <Text fontSize={{ base: "2xl", md: "3xl" }} fontWeight="bold" letterSpacing="tight">
                      {stats.total_workouts}
                    </Text>
                    <HStack spacing={1}>
                      <Icon as={FiActivity} boxSize={3} />
                      <Text fontSize={{ base: "2xs", md: "xs" }} opacity={0.9}>
                        {stats.total_exercises} exercises
                      </Text>
                    </HStack>
                  </VStack>
                  <Box
                    bg="whiteAlpha.200"
                    p={{ base: 2, md: 3 }}
                    borderRadius="16px"
                    backdropFilter="blur(10px)"
                  >
                    <Icon as={FiActivity} boxSize={{ base: 5, md: 6 }} />
                  </Box>
                </HStack>
              </CardBody>
            </Card>
          </SimpleGrid>
        )}

        {/* Independent Trainers Section - solo trainers & gym influencers who self-onboarded */}
        <Card
          bg={bg}
          borderRadius="20px"
          boxShadow="0 20px 40px -12px rgba(0,0,0,0.1)"
          border="1px solid"
          borderColor={borderColor}
          overflow="hidden"
        >
          <CardHeader
            bg={headerBg}
            borderBottom="1px solid"
            borderColor={borderColor}
            py={6}
          >
            <VStack spacing={4} align="stretch">
              <VStack align="start" spacing={1}>
                <Heading size="lg" color={textColor} display="flex" alignItems="center">
                  <Icon as={FiAward} mr={3} color="orange.400" boxSize={6} />
                  Independent Trainers
                </Heading>
                <Text fontSize="sm" color={textColorSecondary}>
                  Solo trainers and gym influencers who signed up on their own — no agency, active immediately
                </Text>
              </VStack>
              <HStack spacing={4} flexWrap={{ base: "wrap", md: "nowrap" }}>
                <InputGroup maxW={{ base: "100%", md: "350px" }} flex="1">
                  <InputLeftElement>
                    <Icon as={FiSearch} color="gray.400" />
                  </InputLeftElement>
                  <Input
                    placeholder="Search by name, email, code or @handle..."
                    value={soloSearch}
                    onChange={(e) => { setSoloSearch(e.target.value); setSoloPage(1); }}
                    bg={inputBg}
                    color={inputColor}
                    border="1px solid"
                    borderColor={inputBorderColor}
                    borderRadius="12px"
                    _hover={{ borderColor: inputHoverBorderColor }}
                    _focus={{ borderColor: "orange.400", boxShadow: inputFocusShadow }}
                    _placeholder={{ color: placeholderColor }}
                  />
                </InputGroup>
                <Select
                  maxW={{ base: "100%", md: "180px" }}
                  value={soloActiveOnly ? "active" : "all"}
                  onChange={(e) => { setSoloActiveOnly(e.target.value === "active"); setSoloPage(1); }}
                  bg={inputBg}
                  color={inputColor}
                  border="1px solid"
                  borderColor={inputBorderColor}
                  borderRadius="12px"
                  _hover={{ borderColor: inputHoverBorderColor }}
                  _focus={{ borderColor: "orange.400", boxShadow: inputFocusShadow }}
                >
                  <option value="all">All Trainers</option>
                  <option value="active">Active Only</option>
                </Select>
              </HStack>
            </VStack>
          </CardHeader>
          <CardBody>
            {soloLoading ? (
              <VStack spacing={4} py={8}>
                <Spinner size="lg" color="orange.400" />
                <Text color={textColorSecondary}>Loading independent trainers...</Text>
              </VStack>
            ) : soloTrainers.length === 0 ? (
              <Alert
                status="info"
                bg={infoAlertBg}
                borderRadius="12px"
                border="1px solid"
                borderColor={infoAlertBorder}
              >
                <AlertIcon color="blue.500" />
                <VStack align="start" spacing={1}>
                  <Text fontWeight="500" color={textColor}>No independent trainers yet</Text>
                  <Text fontSize="sm" color={textColorSecondary}>
                    They appear here as soon as a trainer signs up without an agency code.
                  </Text>
                </VStack>
              </Alert>
            ) : (
              <Box overflowX="auto">
                <Table variant="simple">
                  <Thead>
                    <Tr>
                      <Th>Trainer</Th>
                      <Th>Instagram</Th>
                      <Th>Trainer Code</Th>
                      <Th>Specialization</Th>
                      <Th>Status</Th>
                      <Th isNumeric>Clients</Th>
                      <Th>Joined</Th>
                      <Th>Last Active</Th>
                      <Th>Branded app</Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {soloTrainers.map((trainer) => (
                      <Tr key={trainer.id}>
                        <Td>
                          <HStack spacing={3}>
                            <Avatar name={trainer.name} size="sm" />
                            <VStack align="start" spacing={0}>
                              <Text fontWeight="500" color={textColor}>{trainer.name}</Text>
                              <Text fontSize="sm" color={textColorSecondary}>{trainer.email}</Text>
                            </VStack>
                          </HStack>
                        </Td>
                        <Td>
                          {trainer.instagram_handle ? (
                            <HStack spacing={1}>
                              <Icon as={FiInstagram} color="pink.400" />
                              <Text fontSize="sm" color={textColor}>@{trainer.instagram_handle}</Text>
                            </HStack>
                          ) : (
                            <Text fontSize="sm" color={textColorSecondary}>—</Text>
                          )}
                        </Td>
                        <Td>
                          <Badge colorScheme="purple" fontFamily="mono">{trainer.trainer_code}</Badge>
                        </Td>
                        <Td>
                          <Text fontSize="sm" color={textColor}>
                            {trainer.specialization || '—'}
                          </Text>
                        </Td>
                        <Td>
                          <Badge colorScheme={trainer.is_active ? "green" : "red"}>
                            {trainer.is_active ? "Active" : "Inactive"}
                          </Badge>
                        </Td>
                        <Td isNumeric>
                          <Text fontSize="sm">{trainer.client_count}</Text>
                        </Td>
                        <Td>
                          <Text fontSize="sm">{formatDate(trainer.join_date)}</Text>
                        </Td>
                        <Td>
                          <Text fontSize="sm" color={trainer.last_login ? textColor : textColorSecondary}>
                            {trainer.last_login ? formatDate(trainer.last_login) : 'Never'}
                          </Text>
                        </Td>
                        <Td>
                          <Switch
                            isChecked={trainer.branding_enabled}
                            onChange={(e) =>
                              toggleBranding(trainer.agency_id, e.target.checked, (now) =>
                                setSoloTrainers((prev) =>
                                  prev.map((t) =>
                                    t.agency_id === trainer.agency_id ? { ...t, branding_enabled: now } : t,
                                  ),
                                ),
                              )
                            }
                            colorScheme="orange"
                          />
                        </Td>
                      </Tr>
                    ))}
                  </Tbody>
                </Table>
              </Box>
            )}

            {soloTotalCount > 10 && (
              <HStack justify="space-between" align="center" pt={4}>
                <Text fontSize="sm" color={textColorSecondary}>
                  Showing {((soloPage - 1) * 10) + 1} - {Math.min(soloPage * 10, soloTotalCount)} of {soloTotalCount} trainers
                </Text>
                <HStack spacing={2}>
                  <Button
                    size="sm"
                    variant="outline"
                    colorScheme="orange"
                    borderRadius="10px"
                    isDisabled={soloPage === 1}
                    onClick={() => setSoloPage(soloPage - 1)}
                  >
                    Previous
                  </Button>
                  <Text fontSize="sm" color={textColorSecondary}>
                    {soloPage} of {Math.ceil(soloTotalCount / 10)}
                  </Text>
                  <Button
                    size="sm"
                    variant="outline"
                    colorScheme="orange"
                    borderRadius="10px"
                    isDisabled={soloPage >= Math.ceil(soloTotalCount / 10)}
                    onClick={() => setSoloPage(soloPage + 1)}
                  >
                    Next
                  </Button>
                </HStack>
              </HStack>
            )}
          </CardBody>
        </Card>

        {/* Agencies Section */}
        <Card
          bg={bg}
          borderRadius="20px"
          boxShadow="0 20px 40px -12px rgba(0,0,0,0.1)"
          border="1px solid"
          borderColor={borderColor}
          overflow="hidden"
        >
          <CardHeader 
            bg={headerBg} 
            borderBottom="1px solid" 
            borderColor={borderColor}
            py={6}
          >
            <VStack spacing={4} align="stretch">
              <Heading size="lg" color={textColor} display="flex" alignItems="center">
                <Icon as={FiUsers} mr={3} color="blue.500" boxSize={6} />
                Agencies Management
                </Heading>
              <VStack spacing={4} align="stretch">
                <HStack spacing={4} flexWrap={{ base: "wrap", md: "nowrap" }}>
                  <InputGroup maxW={{ base: "100%", md: "350px" }} flex="1">
                    <InputLeftElement>
                      <Icon as={FiSearch} color="gray.400" />
                    </InputLeftElement>
                    <Input
                      placeholder="Search agencies by name or email..."
                      value={searchTerm}
                      onChange={(e) => handleSearch(e.target.value)}
                      bg={inputBg}
                      color={inputColor}
                      border="1px solid"
                      borderColor={inputBorderColor}
                      borderRadius="12px"
                      _hover={{ borderColor: inputHoverBorderColor }}
                      _focus={{ borderColor: "blue.500", boxShadow: inputFocusShadow }}
                      _placeholder={{ color: placeholderColor }}
                    />
                  </InputGroup>
                  <Select 
                    maxW={{ base: "100%", md: "180px" }}
                    value={showActiveOnly ? "active" : "all"} 
                    onChange={(e) => handleFilterChange(e.target.value === "active")}
                    bg={inputBg}
                    color={inputColor}
                    border="1px solid"
                    borderColor={inputBorderColor}
                    borderRadius="12px"
                    _hover={{ borderColor: inputHoverBorderColor }}
                    _focus={{ borderColor: "blue.500", boxShadow: inputFocusShadow }}
                  >
                    <option value="all">All Agencies</option>
                    <option value="active">Active Only</option>
                  </Select>
                </HStack>
              </VStack>
            </VStack>
          </CardHeader>
          <CardBody>
            {agenciesLoading ? (
              <VStack spacing={4} py={8}>
                <Spinner size="lg" color="brand.500" />
                <Text color={textColorSecondary}>Loading agencies...</Text>
              </VStack>
            ) : agencies.length === 0 ? (
              <Alert 
                status="info" 
                bg={infoAlertBg} 
                borderRadius="12px"
                border="1px solid"
                borderColor={infoAlertBorder}
              >
                <AlertIcon color="blue.500" />
                <VStack align="start" spacing={1}>
                  <Text fontWeight="500" color={textColor}>No agencies found</Text>
                  <Text fontSize="sm" color={textColorSecondary}>Try adjusting your search or filter criteria.</Text>
                </VStack>
              </Alert>
            ) : (
              <Box overflowX="auto">
                <Table variant="simple">
                  <Thead>
                    <Tr>
                      <Th>Agency</Th>
                      <Th>Owner</Th>
                      <Th>Status</Th>
                      <Th>Trainers</Th>
                      <Th>Clients</Th>
                      <Th>Created</Th>
                      <Th>Actions</Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {agencies.map((agency) => (
                      <Tr key={agency.id}>
                        <Td>
                          <VStack align="start" spacing={1}>
                            <Text fontWeight="500" color={textColor}>
                              {agency.name}
                            </Text>
                            <Text fontSize="sm" color={textColorSecondary}>
                              {agency.contact_email}
                            </Text>
                          </VStack>
                        </Td>
                        <Td>
                          <Text fontSize="sm">
                            {agency.owner.first_name} {agency.owner.last_name}
                          </Text>
                        </Td>
                        <Td>
                          <Badge colorScheme={agency.is_active ? "green" : "orange"}>
                            {agency.is_active ? "Active" : "Inactive"}
                          </Badge>
                        </Td>
                        <Td>
                          <Text fontSize="sm">{agency.trainer_count}</Text>
                        </Td>
                        <Td>
                          <Text fontSize="sm">{agency.client_count}</Text>
                        </Td>
                        <Td>
                          <Text fontSize="sm">{formatDate(agency.created_at)}</Text>
                        </Td>
                        <Td>
                          <Button 
                            size="sm" 
                            variant="outline" 
                            colorScheme="blue"
                            leftIcon={<Icon as={FiEye} />}
                            onClick={() => handleViewDetails(agency.id)}
                          >
                            View Details
                          </Button>
                        </Td>
                      </Tr>
                    ))}
                  </Tbody>
                </Table>
              </Box>
            )}
          </CardBody>
        </Card>

        {/* Pagination */}
        {totalCount > 10 && (
          <Card 
            bg={bg} 
            borderRadius="16px"
            boxShadow="0 10px 25px -5px rgba(0,0,0,0.1)"
            border="1px solid"
            borderColor={borderColor}
          >
            <CardBody py={4}>
              <HStack justify="space-between" align="center">
                <Text fontSize="sm" color={textColorSecondary}>
                  Showing {((currentPage - 1) * 10) + 1} - {Math.min(currentPage * 10, totalCount)} of {totalCount} agencies
                </Text>
                <HStack spacing={2}>
                  <Button 
                    size="sm" 
                    variant="outline"
                    colorScheme="blue"
                    borderRadius="10px"
                    isDisabled={currentPage === 1}
                    onClick={() => setCurrentPage(currentPage - 1)}
                  >
                    Previous
                  </Button>
                  <Box 
                    px={3} 
                    py={1} 
                    bg={infoAlertBg}
                    color="blue.500"
                    borderRadius="8px"
                    fontSize="sm"
                    fontWeight="semibold"
                  >
                    {currentPage}
                  </Box>
                  <Text fontSize="sm" color={textColorSecondary}>
                    of {Math.ceil(totalCount / 10)}
                  </Text>
                  <Button 
                    size="sm" 
                    variant="outline"
                    colorScheme="blue"
                    borderRadius="10px"
                    isDisabled={currentPage >= Math.ceil(totalCount / 10)}
                    onClick={() => setCurrentPage(currentPage + 1)}
                  >
                    Next
                  </Button>
                </HStack>
              </HStack>
            </CardBody>
          </Card>
        )}

        {/* Agency Details Modal */}
        <Modal isOpen={isOpen} onClose={onClose} size={{ base: "full", md: "4xl" }} scrollBehavior="inside">
          <ModalOverlay />
          <ModalContent mx={{ base: 0, md: 4 }} my={{ base: 0, md: 4 }} bg={bg}>
            <ModalHeader>
              <VStack align="start" spacing={2}>
                <Text fontSize={{ base: "lg", md: "xl" }}>Agency Details</Text>
                {agencyDetails && (
                  <Badge colorScheme={agencyDetails.is_active ? "green" : "orange"} size="lg">
                    {agencyDetails.is_active ? "Active" : "Inactive"}
                  </Badge>
                )}
              </VStack>
            </ModalHeader>
            <ModalCloseButton />
            <ModalBody px={{ base: 4, md: 6 }}>
              {detailsLoading ? (
                <Flex justify="center" align="center" minH="300px">
                  <VStack spacing={4}>
                    <Spinner size="xl" color="brand.500" />
                    <Text color={textColorSecondary}>Loading agency details...</Text>
                  </VStack>
                </Flex>
              ) : agencyDetails ? (
                <VStack spacing={6} align="stretch">
                  {/* Agency Information */}
                  <Box>
                    <Heading size="md" color={textColor} mb={4}>
                      Agency Information
                    </Heading>
                    <VStack spacing={4} align="stretch">
                      <Box>
                        <Text fontSize="sm" color={textColorSecondary} mb={1}>
                          Agency Name
                        </Text>
                        <Text fontWeight="600" color={textColor}>
                          {agencyDetails.name}
                        </Text>
                      </Box>
                      <Box>
                        <Text fontSize="sm" color={textColorSecondary} mb={1}>
                          Description
                        </Text>
                        <Text color={textColor}>
                          {agencyDetails.description || "No description provided"}
                        </Text>
                      </Box>
                      <Box>
                        <Text fontSize="sm" color={textColorSecondary} mb={1}>
                          <Icon as={FiMapPin} mr={1} />
                          Address
                        </Text>
                        <Text color={textColor}>
                          {agencyDetails.address || "No address provided"}
                        </Text>
                      </Box>
                      <Box>
                        <Text fontSize="sm" color={textColorSecondary} mb={1}>
                          <Icon as={FiMail} mr={1} />
                          Contact Email
                        </Text>
                        <Text color={textColor}>
                          {agencyDetails.contact_email}
                        </Text>
                      </Box>
                      {agencyDetails.contact_phone && (
                        <Box>
                          <Text fontSize="sm" color={textColorSecondary} mb={1}>
                            <Icon as={FiPhone} mr={1} />
                            Phone Number
                          </Text>
                          <Text color={textColor}>
                            {agencyDetails.contact_phone}
                          </Text>
                        </Box>
                      )}
                      <Box>
                        <Text fontSize="sm" color={textColorSecondary} mb={1}>
                          <Icon as={FiCalendar} mr={1} />
                          Created Date
                        </Text>
                        <Text color={textColor}>
                          {formatDate(agencyDetails.created_at)}
                        </Text>
                      </Box>
                    </VStack>
                  </Box>

                  <Divider />

                  {/* Owner Information */}
                  <Box>
                    <Heading size="md" color={textColor} mb={4}>
                      Owner Information
                    </Heading>
                    <HStack spacing={4}>
                      <Avatar 
                        name={`${agencyDetails.owner.first_name} ${agencyDetails.owner.last_name}`} 
                        size="lg"
                      />
                      <VStack align="start" spacing={2}>
                        <Text fontWeight="600" color={textColor}>
                          {agencyDetails.owner.first_name} {agencyDetails.owner.last_name}
                        </Text>
                        <Text color={textColorSecondary}>
                          {agencyDetails.owner.email}
                        </Text>
                      </VStack>
                    </HStack>
                  </Box>

                  <Divider />

                  {/* Statistics */}
                  <Box>
                    <Heading size="md" color={textColor} mb={4}>
                      Agency Statistics
                    </Heading>
                    <SimpleGrid columns={{ base: 2, lg: 4 }} spacing={4}>
                      <Stat textAlign="center">
                        <StatLabel color={textColorSecondary} fontSize="xs">Trainers</StatLabel>
                        <StatNumber color="blue.500" fontSize={{ base: "lg", md: "xl" }}>
                          {agencyDetails.agency_stats.total_trainers}
                        </StatNumber>
                        <StatHelpText color="green.500" fontSize="xs">
                          {agencyDetails.agency_stats.active_trainers} active
                        </StatHelpText>
                      </Stat>
                      <Stat textAlign="center">
                        <StatLabel color={textColorSecondary} fontSize="xs">Clients</StatLabel>
                        <StatNumber color="purple.500" fontSize={{ base: "lg", md: "xl" }}>
                          {agencyDetails.agency_stats.total_clients}
                        </StatNumber>
                        <StatHelpText color="green.500" fontSize="xs">
                          {agencyDetails.agency_stats.active_clients} active
                        </StatHelpText>
                      </Stat>
                      <Stat textAlign="center">
                        <StatLabel color={textColorSecondary} fontSize="xs">Workouts</StatLabel>
                        <StatNumber color="orange.500" fontSize={{ base: "lg", md: "xl" }}>
                          {agencyDetails.agency_stats.total_workouts}
                        </StatNumber>
                        <StatHelpText color={textColorSecondary} fontSize="xs">
                          {agencyDetails.agency_stats.monthly_workouts} this month
                        </StatHelpText>
                      </Stat>
                      <Stat textAlign="center">
                        <StatLabel color={textColorSecondary} fontSize="xs">Exercises</StatLabel>
                        <StatNumber color="green.500" fontSize={{ base: "lg", md: "xl" }}>
                          {agencyDetails.agency_stats.total_exercises}
                        </StatNumber>
                      </Stat>
                    </SimpleGrid>
                  </Box>

                  {/* Trainers List */}
                  {agencyDetails.trainers.length > 0 && (
                    <>
                      <Divider />
                      <Box>
                        <Heading size="md" color={textColor} mb={4}>
                          Trainers ({agencyDetails.trainers.length})
                        </Heading>
                        <VStack spacing={3} align="stretch">
                          {agencyDetails.trainers.map((trainer) => (
                            <Card key={trainer.id} variant="outline" bg={inputBg}>
                              <CardBody py={3}>
                                <HStack justify="space-between">
                                  <HStack spacing={3}>
                                    <Avatar name={trainer.name} size="sm" />
                                    <VStack align="start" spacing={0}>
                                      <Text fontWeight="500" color={textColor}>
                                        {trainer.name}
                                      </Text>
                                      <Text fontSize="sm" color={textColorSecondary}>
                                        {trainer.email}
                                      </Text>
                                      <Text fontSize="xs" color={textColorSecondary}>
                                        {trainer.specialization || "No specialization"}
                                      </Text>
                                    </VStack>
                                  </HStack>
                                  <VStack align="end" spacing={1}>
                                    <Badge colorScheme={trainer.is_active ? "green" : "red"} size="sm">
                                      {trainer.is_active ? "Active" : "Inactive"}
                                    </Badge>
                                    <Text fontSize="xs" color={textColorSecondary}>
                                      {trainer.client_count} clients
                                    </Text>
                                    <Text fontSize="xs" color={textColorSecondary}>
                                      {trainer.experience_years} years exp.
                                    </Text>
                                  </VStack>
                                </HStack>
                              </CardBody>
                            </Card>
                          ))}
                        </VStack>
                      </Box>
                    </>
                  )}

                  {/* Branded app entitlement (the "your own branded app" tier) */}
                  <Divider />
                  <Box>
                    <Heading size="md" color={textColor} mb={2}>
                      Branded app
                    </Heading>
                    <HStack justify="space-between">
                      <VStack align="start" spacing={0}>
                        <Text color={textColor}>
                          {agencyDetails.branding_enabled
                            ? `Enabled${agencyDetails.brand_name ? ` — "${agencyDetails.brand_name}"` : " — brand not set yet"}`
                            : "Disabled"}
                        </Text>
                        <Text fontSize="sm" color={textColorSecondary}>
                          Lets this agency put its own name, logo and colours in the client app, emails and notifications.
                        </Text>
                      </VStack>
                      <Switch
                        isChecked={agencyDetails.branding_enabled}
                        onChange={(e) =>
                          agencyDetails &&
                          toggleBranding(agencyDetails.id, e.target.checked, (now) =>
                            setAgencyDetails({ ...agencyDetails, branding_enabled: now }),
                          )
                        }
                        colorScheme="brand"
                        size="lg"
                      />
                    </HStack>
                  </Box>
                </VStack>
              ) : (
                <Alert
                  status="error"
                  bg={errorAlertBg}
                  borderRadius="12px"
                  border="1px solid"
                  borderColor={errorAlertBorder}
                >
                  <AlertIcon color="red.500" />
                  <VStack align="start" spacing={1}>
                    <Text fontWeight="500" color={textColor}>Failed to load agency details</Text>
                    <Text fontSize="sm" color={textColorSecondary}>Please try again later.</Text>
                  </VStack>
                </Alert>
              )}
            </ModalBody>

            <ModalFooter>
              <Button colorScheme="blue" mr={3} onClick={onClose}>
                Close
              </Button>
            </ModalFooter>
          </ModalContent>
        </Modal>
      </VStack>
    </Box>
  );
}

export default AdminDashboard;